<template>
  <form class="contact-form"
        novalidate
        @submit.prevent="handleSubmit">
    <div class="form-field">
      <label for="email-fullName"
             class="form-label">
        {{ fullNameLabel }} <span class="required">*</span>
      </label>
      <input id="email-fullName"
             v-model="fullName"
             type="text"
             class="form-input"
             :class="{ 'form-input-error': errors.fullName }"
             :placeholder="placeholderFullName"
             v-bind="fullNameAttrs">
      <UIError :message="errors.fullName ? t(errors.fullName) : undefined" />
    </div>
    <div class="form-field">
      <label for="email"
             class="form-label">
        {{ emailLabel }} <span class="required">*</span>
      </label>
      <input id="email"
             v-model="email"
             type="email"
             inputmode="email"
             autocomplete="email"
             class="form-input"
             :class="{ 'form-input-error': errors.email }"
             :placeholder="placeholderEmail"
             v-bind="emailAttrs">
      <UIError :message="errors.email ? t(errors.email) : undefined" />
    </div>
    <!-- region field removed for solascriptura.md -->
    <div v-if="showLocality"
         class="form-field">
      <label for="email-locality"
             class="form-label">{{ localityLabel }}</label>
      <input id="email-locality"
             v-model="locality"
             type="text"
             class="form-input"
             :placeholder="placeholderLocality"
             v-bind="localityAttrs">
    </div>
    <!-- <div class="form-field">
      <label for="email-message"
             class="form-label">{{ messageLabel }}</label>
      <textarea id="email-message"
                v-model="message"
                v-autosize
                class="form-textarea"
                :class="{ 'form-input-error': errors.message }"
                :placeholder="placeholderMessage"
                rows="1"
                v-bind="messageAttrs" />
      <UIError :message="errors.message ? t(errors.message) : undefined" />
    </div> -->
    <div v-if="showPrivacyCheckbox">
      <div class="form-checkbox">
        <input id="email-privacy"
               v-model="privacyAccepted"
               type="checkbox"
               class="form-checkbox-input"
               v-bind="privacyAcceptedAttrs">
        <label for="email-privacy"
               class="form-checkbox-label">
          {{ privacyAgree }}
          <NuxtLink :to="`/${locale}/privacy-policy`"
                    class="privacy-link">{{ privacyPolicyLink }}</NuxtLink>.
        </label>
      </div>
      <div>
        <UIError :message="errors.privacyAccepted ? t(errors.privacyAccepted) : undefined"
                 inline
                 extra-class="pt-4" />
      </div>
    </div>
    <div v-if="submitError"
         class="form-submit-error">
      <UIError :message="submitError" />
    </div>
    <div class="form-submit">
      <UIButton variant="primary"
                submit
                rounded="8"
                class="w-full contact-submit-button"
                :disabled="loading">
        {{ loading ? t('index.contactForm.sending') : submitButtonText }}
      </UIButton>
    </div>
  </form>
</template>

<script setup lang="ts">
import { watch, computed } from 'vue'
import { useContactEmailForm } from '@/composables/contact/useContactEmailForm'
import RegionInput from '@/components/UI/region/RegionInput.vue'
import type { RegionOption } from '@/composables/region'
import { ErrorHandler, NetworkError } from '@/infrastructure/errors'
import type { ContactFormData } from '@/composables/contact/contact-form.types'

interface Props {
  showLocality?: boolean
  showRegion?: boolean
  showPrivacyCheckbox?: boolean
  submitButtonText?: string
  regionOptions?: RegionOption[]
  regionLoading?: boolean
  regionIsoCode?: string
  /** ID курса — для заявок зі сторінки деталей курса */
  courseId?: string
}

const { t, locale } = useI18n()
const props = withDefaults(defineProps<Props>(), {
  showLocality: true,
  showRegion: true,
  showPrivacyCheckbox: true,
  submitButtonText: undefined,
  regionOptions: () => [],
  regionLoading: false,
  regionIsoCode: undefined,
  courseId: undefined,
})

watch(locale, () => {}, { immediate: true })

const fullNameLabel = computed(() => { locale.value; return t('index.contactForm.fullNameLabel') })
const emailLabel = computed(() => { locale.value; return t('index.contactForm.emailLabel') })
const localityLabel = computed(() => { locale.value; return t('index.contactForm.localityLabel') })
const regionLabel = computed(() => { locale.value; return t('index.contactForm.regionLabel') })
const messageLabel = computed(() => { locale.value; return t('index.contactForm.messageLabel') })
const placeholderFullName = computed(() => { locale.value; return t('index.contactForm.placeholderFullName') })
const placeholderEmail = computed(() => { locale.value; return t('index.contactForm.placeholderEmail') })
const placeholderLocality = computed(() => { locale.value; return t('index.contactForm.placeholderLocality') })
const placeholderRegion = computed(() => { locale.value; return t('index.contactForm.placeholderRegion') })
const placeholderMessage = computed(() => { locale.value; return t('index.contactForm.placeholderMessage') })
const privacyAgree = computed(() => { locale.value; return t('index.contactForm.privacyAgree') })
const privacyPolicyLink = computed(() => { locale.value; return t('index.contactForm.privacyPolicyLink') })
const submitButtonText = computed(() => { locale.value; return props.submitButtonText ?? t('index.contactForm.submitButton') })

const {
  fullName, fullNameAttrs,
  email, emailAttrs,
  locality, localityAttrs,
  region, regionAttrs,
  message, messageAttrs,
  privacyAccepted, privacyAcceptedAttrs,
  errors, showRegion,
  submitForm, loading, submitError,
} = useContactEmailForm({
  requirePrivacyAccepted: props.showPrivacyCheckbox,
  showRegion: props.showRegion,
  courseId: props.courseId,
})

const emit = defineEmits<{
  submit: [data: ContactFormData]
  error: [error: Error]
}>()

const handleSubmit = async () => {
  try {
    await submitForm(
      (data) => emit('submit', data),
      (error) => emit('error', ErrorHandler.handle(error)),
      (error, statusCode) => {
        if (error instanceof NetworkError) return t('common.networkError')
        if (statusCode === 429) return t('index.contactForm.rateLimitError')
        if (statusCode === 400) return t('index.contactForm.badRequestError')
        return error.message ?? t('index.contactForm.submitError')
      }
    )
  } catch {}
}
</script>

<style scoped>
.contact-form { display: flex; flex-direction: column; gap: 20px; }
.form-field { display: flex; flex-direction: column; gap: 8px; }
.form-label { font-family: var(--font-inter); font-weight: 500; font-size: 16px; color: var(--color-dark-blue); }
.required { color: var(--color-error); }
.form-input, .form-textarea, .form-select {
  width: 100%; padding: 14px 16px; font-family: var(--font-inter); font-size: 16px;
  color: var(--color-dark-blue); border: 1px solid #E5E7EB; border-radius: 8px;
  transition: border-color 0.2s; background: #fff;
}
.form-select { cursor: pointer; appearance: auto; }
.form-input-error { border: 2px solid var(--color-error); }
.form-textarea { resize: vertical; min-height: 48px; }
.form-checkbox { display: flex; align-items: flex-start; gap: 12px; }
.form-checkbox-input { margin-top: 2px; width: 18px; height: 18px; cursor: pointer; accent-color: #09AFEA; }
.form-checkbox-label { font-family: var(--font-inter); font-size: 14px; line-height: 1.6; color: #252840; cursor: pointer; }
.privacy-link { color: #09AFEA; text-decoration: underline; transition: color 0.2s; }
.privacy-link:hover { color: #19E089; }
.form-submit-error { margin-top: 8px; }
.form-submit { margin-top: 8px; }
.contact-submit-button { padding: 16px 32px; }
@media (max-width: 768px) { .contact-submit-button { text-align: center; justify-content: center !important; } }
@media (max-width: 480px) { .contact-submit-button { padding-top: 11px; padding-bottom: 11px; } }
@media (max-width: 575px) { .form-input, .form-textarea { font-size: 16px; padding: 12px 14px; } }
</style>
